import { MotionConfig } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { BatteryIcon, WifiIcon } from './Icons';

interface IPhone16FrameProps {
  children: React.ReactNode;
}

// 毎分時計が更新されたときにIPhone16Frame全体（＝アプリ全体）が再描画されるのを防ぐため、
// 時計を別コンポーネントに分離してメモ化
const StatusBarClock: React.FC = React.memo(() => {
  const [time, setTime] = useState('9:41');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setTime(`${hours}:${minutes}`);
    };

    updateTime();

    let timeoutId: ReturnType<typeof setTimeout>;
    const syncTime = () => {
      updateTime();
      const now = new Date();
      // 次の分の境界（00秒）までのミリ秒を計算
      const delay = 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
      timeoutId = setTimeout(syncTime, delay);
    };

    const initialDelay = 60000 - (new Date().getSeconds() * 1000 + new Date().getMilliseconds());
    timeoutId = setTimeout(syncTime, initialDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return <span className="font-semibold">{time}</span>;
});

export const IPhone16Frame: React.FC<IPhone16FrameProps> = ({ children }) => {
  return (
    <div className="iphone-frame-root min-h-screen w-full bg-gradient-to-br from-neutral-900 via-neutral-950 to-neutral-900 flex items-center justify-center p-0 md:p-6 lg:p-8 font-sans overflow-hidden">
      {/* 
        iPhone16 モックアップコンテナ
        モバイル環境 (md未満) では全画面になり、ベゼルやボタンは非表示
        デスクトップ環境 (md以上) ではiPhone16の形をした高精度のモックを表示
        md以上のプレビュー寸法は :root の --iphone16-width / --iphone16-height を参照
      */}
      <div className="iphone-frame-shell relative w-full h-[100dvh] md:h-[min(var(--iphone16-height),calc(100dvh-3rem))] md:w-[min(var(--iphone16-width),calc((100dvh-3rem)*0.4613))] bg-black md:rounded-[55px] md:shadow-[0_0_0_12px_#1c1c1e,0_0_0_13px_#2c2c2e,0_25px_50px_-12px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden transition-all duration-500 ease-out md:border-[1px] md:border-neutral-800">
        {/* ディスプレイエリア */}
        <div className="iphone-frame-display relative w-full h-full flex flex-col bg-transparent overflow-hidden md:rounded-[51px]">
          {/* Dynamic Island (iPhone16の特徴、デスクトップのみ表示) */}
          <div className="desktop-chrome hidden md:flex absolute top-3 left-1/2 -translate-x-1/2 w-[110px] h-[30px] bg-black rounded-full z-50 items-center justify-between px-3 overflow-hidden shadow-inner border border-neutral-900">
            {/* 近接センサー・カメラレンズの反射表現 */}
            <div className="w-3.5 h-3.5 bg-[radial-gradient(circle_at_center,#1c2837_0%,#090d12_100%)] rounded-full opacity-60" />
            <div className="w-1.5 h-1.5 bg-[#081a2e] rounded-full border border-neutral-800" />
          </div>

          {/* iOS ステータスバー (デスクトップのモック表示時のみ表示、モバイル実機ではネイティブのバーを使用するため非表示) */}
          <div className="desktop-chrome hidden md:flex justify-between items-center absolute top-0 inset-x-0 h-[54px] px-6 pt-4 pb-2 text-[13px] font-semibold tracking-tight select-none z-40 bg-transparent text-neutral-800 dark:text-neutral-200 pointer-events-none">
            {/* 時間 */}
            <StatusBarClock />

            {/* ステータスアイコン (右側) */}
            <div className="flex items-center space-x-1.5">
              {/* アンテナ */}
              <div className="flex items-end space-x-[2px] h-[10px]">
                <div className="w-[2.5px] h-[30%] bg-current rounded-sm" />
                <div className="w-[2.5px] h-[50%] bg-current rounded-sm" />
                <div className="w-[2.5px] h-[75%] bg-current rounded-sm" />
                <div className="w-[2.5px] h-[100%] bg-current rounded-sm" />
              </div>

              <span className="text-[10px] font-bold">5G</span>
              <WifiIcon size={14} strokeWidth={2} />
              <BatteryIcon size={18} strokeWidth={1.5} />
            </div>
          </div>

          {/* メインのアプリケーションコンテンツ */}
          <div className="flex-1 w-full h-full min-h-0 overflow-hidden relative">
            <MotionConfig reducedMotion="user">{children}</MotionConfig>
          </div>

          {/* iOS ホームインジケーターバー (デスクトップのモック表示時のみ表示、モバイル実機ではネイティブのインジケーターを使用するため非表示) */}
          <div className="desktop-chrome hidden md:block absolute bottom-2 left-1/2 -translate-x-1/2 w-[130px] h-[5px] bg-neutral-500/30 dark:bg-neutral-400/30 rounded-full z-50 pointer-events-none" />
        </div>

        {/* --- デスクトップ用：iPhone16 物理ボタンの装飾 --- */}
        {/* 左側：アクションボタン (iPhone16) */}
        <div className="desktop-chrome hidden md:block absolute left-[-14px] top-[145px] w-[3px] h-[28px] bg-gradient-to-r from-neutral-800 to-neutral-600 rounded-l-md border-y border-l border-neutral-700 shadow-md" />
        {/* 左側：音量＋ボタン */}
        <div className="desktop-chrome hidden md:block absolute left-[-14px] top-[195px] w-[3px] h-[55px] bg-gradient-to-r from-neutral-800 to-neutral-600 rounded-l-md border-y border-l border-neutral-700 shadow-md" />
        {/* 左側：音量－ボタン */}
        <div className="desktop-chrome hidden md:block absolute left-[-14px] top-[265px] w-[3px] h-[55px] bg-gradient-to-r from-neutral-800 to-neutral-600 rounded-l-md border-y border-l border-neutral-700 shadow-md" />

        {/* 右側：サイドボタン (電源) */}
        <div className="desktop-chrome hidden md:block absolute right-[-14px] top-[195px] w-[3px] h-[75px] bg-gradient-to-l from-neutral-800 to-neutral-600 rounded-r-md border-y border-r border-neutral-700 shadow-md" />

        {/* 右側：カメラコントロール (iPhone16の新機能：少し埋め込まれた感触のボタン) */}
        <div className="desktop-chrome hidden md:block absolute right-[-13px] top-[305px] w-[2px] h-[46px] bg-neutral-900 rounded-r-sm border-y border-r border-neutral-800 shadow-inner">
          <div className="w-full h-full bg-neutral-800/80 rounded-r-sm opacity-60" />
        </div>
      </div>
    </div>
  );
};
